/* eslint-disable max-len, jsx-a11y/anchor-is-valid */
import * as React from 'react';
import Link from 'next/link';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/util/cn';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';
import { SubNav, type SubNavItem } from '@/components/common/SubNav';

// Shared header for region-level production pages. Composes:
//   1. Optional Country › Region breadcrumb — hidden on mobile,
//      rendered when `country` is set.
//   2. Optional small-caps eyebrow above the H1 (season tags, etc.).
//   3. H1 — the `heading` the caller supplies (e.g. "Colorado Snow Report").
//   4. SubNav tabs — full-bleed on mobile, pill-capsule on md+.
//
// Production peer: `src/components/region/RegionHeaderNav/` (1 caller).
// That stays in place until the region detail page retrofits — then it
// gets replaced by this component in the page's chrome.

export type RegionHeaderProps = {
  /** When set, renders the Country › Region breadcrumb. Omit to hide. */
  country?: string;
  regionName: string;
  /**
   * The complete H1 text. Comes from the page's SEO hero title
   * (`hero.title` in the page's `.server.ts`), which carries the head term
   * the template ranks for — "Colorado Ski Resorts", not "Colorado Guide".
   * Falls back to the bare region name.
   */
  heading?: string;
  /** Small-caps line above the H1. Useful for season tags or qualifiers. */
  eyebrow?: string;
  subNav: SubNavItem[];
  /** Link href for breadcrumb country segment. Defaults to '#'. */
  breadcrumbHref?: string;
  className?: string;
};

const Crumbs = ({
  country, regionName, href = '#',
}: {
  country: string;
  regionName: string;
  href?: string;
}) => (
  <nav className="mb-3 flex flex-wrap items-center gap-1 text-xs text-muted-foreground md:text-sm">
    <Link href={href} className="hover:text-foreground">{country}</Link>
    <ChevronRight size={14} className="fill-none text-muted-foreground/60" />
    <span className="font-medium text-foreground">{regionName}</span>
  </nav>
);

export const RegionHeader = ({
  country,
  regionName,
  heading,
  eyebrow,
  subNav,
  breadcrumbHref,
  className,
}: RegionHeaderProps) => (
  <header className={cn('mb-6', className)}>
    {/* Mobile gets a small horizontal inset on the title block
        (breadcrumb + eyebrow + H1) so it doesn't crash against the
        viewport edge; the SubNav below stays full-bleed. */}
    <div className="max-md:px-4">
      {country && <Crumbs country={country} regionName={regionName} href={breadcrumbHref} />}
      <div className="mb-5">
        {eyebrow && (
          <Text variant="subtle" className="mb-1 text-xs font-bold uppercase tracking-widest text-muted-foreground">
            {eyebrow}
          </Text>
        )}
        <Heading
          level={1}
        >
          {heading || regionName}
        </Heading>
      </div>
    </div>
    {/* SubNav stays inset by the parent container's horizontal padding —
        sits visually flush with the body content beneath it. */}
    <SubNav items={subNav} />
  </header>
);
